import { useCallback, useEffect, useState } from 'react';
import type { ProjectDetail } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Charge le détail d'un projet transverse (ADR-0024) et ses objets membres (vidéos, images, modèles).
 * Les `remove*` retirent un objet puis rafraîchissent le détail pour tenir listes et compteurs à jour.
 * Une ressource étrangère ou absente renvoie 404 : on l'expose via `notFound`, sans écran brutal.
 */
export function useProjectDetail(projectId: string) {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setProject(await apiFetch<ProjectDetail>(`/projects/${projectId}`));
      setNotFound(false);
    } catch {
      setProject(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const removeVideo = useCallback(
    async (videoId: string) => {
      await apiFetch(`/projects/${projectId}/videos/${videoId}`, { method: 'DELETE' });
      await refresh();
    },
    [projectId, refresh],
  );

  const removeImage = useCallback(
    async (imageId: string) => {
      await apiFetch(`/projects/${projectId}/images/${imageId}`, { method: 'DELETE' });
      await refresh();
    },
    [projectId, refresh],
  );

  const removeModel = useCallback(
    async (modelId: string) => {
      await apiFetch(`/projects/${projectId}/models/${modelId}`, { method: 'DELETE' });
      await refresh();
    },
    [projectId, refresh],
  );

  return { project, loading, notFound, removeVideo, removeImage, removeModel, refresh };
}
